import { Location } from '@angular/common';
import { HttpErrorResponse, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, finalize, switchMap, tap } from 'rxjs/operators';

import { Logger, ModalManager, NavigationEvent, NavigationEvents, NavigationService } from '@app/services';
import { SessionErrorHandler } from '@services/interceptors/session-error-handler/session-error.handler';
import { NavigationHelperService } from '@services/navigation/navigation.helper.service';
import { PtrabSessionManager } from '@services/ptrab-session.manager.service';
import { getPtrabUrlFromMap } from '@shared/enums/pages/pages.urls';
import { MSafeAny } from '@shared/models/safe-any/safe-any.model';
import { decodeResponse } from '@shared/utils/utils';

import { MDialogAlertData, MDialogService } from '@mercadona/components-musi/dialog';
import { MTranslateService } from '@mercadona/core/translate';

import { PAGES, PTRAB_PAGES } from '@/enums/pages.enum';
import { ErrorCodes, ErrorMessages } from '@/models/error/error.model';
import { NETWORK_USE_CASES, NetworkUseCases } from '@/use-cases/network/network.use-cases.contract';
import { UserUseCases, USER_USE_CASES } from '@/use-cases/user/user.use-cases.contract';

@Injectable({
  providedIn: 'root'
})
export class PtrabSessionInterceptor implements HttpInterceptor {
  private logger = new Logger('PtrabSessionInterceptor');
  compWindow: MSafeAny = window;

  #userUseCases: UserUseCases = inject(USER_USE_CASES);
  #dialogService: MDialogService = inject(MDialogService);
  private networkService: NetworkUseCases = inject(NETWORK_USE_CASES);

  constructor(
    private navigationService: NavigationService,
    public location: Location,
    private ptrabSessionManager: PtrabSessionManager,
    private sessionErrorHandler: SessionErrorHandler,
    private translate: MTranslateService,
    private navHelperService: NavigationHelperService,
    private modalManager: ModalManager
  ) {}

  intercept(request: HttpRequest<MSafeAny>, next: HttpHandler): Observable<MSafeAny> {
    return next.handle(request).pipe(
      catchError((err) => this.onError(err)),
      finalize(() => {
        this.sessionErrorHandler.enableToast();
      })
    );
  }

  onError(responseRaw: MSafeAny) {
    const error: HttpErrorResponse = decodeResponse(responseRaw);
    this.networkService.doIfConnection(async () => {
      this.logger.error(error);

      const isUnauthorized = this.isUnauthorized(error);
      const isBlocked = this.isBlocked(error);
      this.ptrabSessionManager.setIsUnauthorized(isUnauthorized);
      this.ptrabSessionManager.setIsBlocked(isBlocked);

      if (isUnauthorized || isBlocked) {
        if (this.isPtrabSection()) {
          this.modalManager.dismissMatModal();
          this.getConfirmSessionExpiredDialog$(isBlocked, isUnauthorized).subscribe();
          return;
        }
        this.goToRedirectPage(isBlocked, isUnauthorized);
      }
    });

    this.sessionErrorHandler.handleExternalError(error);
    return throwError(() => responseRaw);
  }

  private goToRedirectPage(isBlocked: boolean, isUnauthorized: boolean) {
    if (isBlocked || this.isRedirectUnauthorized(isUnauthorized)) {
      const navEvent = this.getRedirectionEvent();

      if (navEvent) {
        this.navigationService.navigate(navEvent);
      }
    }
  }

  /* Método para cambios o acceder a otra sección del ptrab */
  private isPtrabSection() {
    const currentWindowUrl = this.compWindow.location.pathname;
    const nextUrl = this.navHelperService.getNextUrl();

    return (
      (currentWindowUrl.includes('/ptrab') &&
        !currentWindowUrl.includes('/ptrab/login') &&
        nextUrl.includes('/ptrab') &&
        !nextUrl.includes('/ptrab/login')) ||
      (currentWindowUrl.includes('/personal-data') && nextUrl.includes('/personal-data'))
    );
  }

  /* Método para acceder desde el ptrab a una sección que no sea del ptrab */
  private isRedirectUnauthorized(isUnauthorized: boolean) {
    const nextUrl = this.navHelperService.getNextUrl();
    const currentWindowUrl = this.compWindow.location.pathname;
    return (
      isUnauthorized &&
      ((!currentWindowUrl.includes('/personal-data') && !currentWindowUrl.includes('/ptrab')) ||
        nextUrl.includes('/personal-data') ||
        nextUrl.includes('/ptrab'))
    );
  }

  private getConfirmSessionExpiredDialog$(isBlocked: boolean, isUnauthorized: boolean): Observable<any> {
    return this.translate
      .selectTranslate(['SESSION.NOT_AVAILABLE.DESC', 'SESSION.NOT_AVAILABLE.TITLE', 'COMMON.ACCEPT'])
      .pipe(
        switchMap((texts) => {
          const dialogConfirmDialogData: MDialogAlertData = {
            message: texts[0],
            infoLabel: texts[1],
            buttonLabel: texts[2]
          };

          const dialog = this.#dialogService.openAlert(dialogConfirmDialogData, true);
          dialog.componentRef?.location.nativeElement.classList.add('custom-button-warning');
          return dialog.afterClosed();
        }),
        tap(() => {
          this.goToRedirectPage(isBlocked, isUnauthorized);
        })
      );
  }

  isUnauthorized(response: HttpErrorResponse) {
    const isCodeUnauthorized = response.status === ErrorCodes.UNAUTHORIZED;
    return isCodeUnauthorized && response.error.code === ErrorMessages.PTRAB_UNAUTHORIZED;
  }

  isBlocked(response: HttpErrorResponse) {
    const isForbidden = response.status === ErrorCodes.FORBIDDEN;
    return isForbidden && response.error.code === ErrorMessages.PTRAB_BLOCKED;
  }

  private isSectionMaintenance(response: HttpErrorResponse) {
    const isMaintenance = response.status === ErrorCodes.SECTION_MAINTENANCE;
    return isMaintenance && response.error.code === ErrorMessages.PERM_DEACTIVATED;
  }

  private getRedirectionEvent(): NavigationEvent | undefined {
    const currentLocation = this.location.path();
    const ptrabLoginRoute = getPtrabUrlFromMap(PTRAB_PAGES.PTRAB_LOGIN);

    if (currentLocation === ptrabLoginRoute) {
      return;
    }

    return this.#userUseCases.hasAccessToPtrabSections()
      ? new NavigationEvent(NavigationEvents.Push, { path: ptrabLoginRoute })
      : new NavigationEvent(NavigationEvents.SetRoot, { path: PAGES.HOME });
  }
}
